import { type CSSProperties, useRef, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { SemenDto } from '@/services/semen-service/types';

import { EditSemenForm } from '../edit-semen-form';
import { EditSemenFormValues } from '../edit-semen-form/types';

type EditSemenDialogProps = {
  visible: boolean;
  onHide: () => void;
  onCancel?: () => void;
  semen?: SemenDto;
  rows?: SemenDto[];
  header?: string;
  className?: string;
  style?: CSSProperties;
  onSuccess?: (data: EditSemenFormValues) => void;
  onError?: (error: unknown) => void;
};

export const EditSemenDialog = ({
  visible,
  onHide,
  onCancel,
  semen,
  rows,
  header,
  className,
  style,
  onSuccess,
  onError,
}: EditSemenDialogProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();
  const [baseDialogContainer, setBaseDialogContainer] =
    useState<HTMLElement | null>(null);
  const isBaseUi = useFeatureFlag('edit_semen_prime_to_base_ui');

  if (isBaseUi) {
    return (
      <UiDialog
        open={visible}
        onOpenChange={(open) => {
          if (!open) onHide();
        }}
      >
        <DialogContent
          preflight={false}
          ref={setBaseDialogContainer}
          className={
            className
              ? 'z-[2000] md:w-[90vw] md:max-w-[1400px]'
              : 'z-[2000] md:max-w-[1000px]'
          }
          overlayClassName="z-[2000]"
        >
          <DialogHeader className="preflight">
            <DialogTitle>{header ?? t('semen.editSemen')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <EditSemenForm
              visible={visible}
              onCancel={onCancel ?? onHide}
              semen={semen}
              rows={rows}
              onSuccess={onSuccess}
              onError={onError}
              calendarContainer={baseDialogContainer ?? undefined}
            />
          </DialogBody>
        </DialogContent>
      </UiDialog>
    );
  }

  return (
    <Dialog
      ref={dialogRef}
      header={header ?? t('semen.editSemen')}
      visible={visible}
      onHide={onHide}
      modal
      className={className ?? 'w-[1000px] max-w-[95vw]'}
      style={style}
      onShow={() =>
        setDialogContainer(dialogRef.current?.getMask() ?? undefined)
      }
    >
      <EditSemenForm
        visible={visible}
        onCancel={onCancel ?? onHide}
        semen={semen}
        rows={rows}
        onSuccess={onSuccess}
        onError={onError}
        calendarContainer={dialogContainer}
      />
    </Dialog>
  );
};
